import type { ResourceLibraryItem } from '../types/resourceLibraryItems';
import { isAssetItem } from '../types/resourceLibraryItems';
import type { SearchResult } from '../types/search';

function getAssetPaths(item: ResourceLibraryItem): string[] {
    if (!isAssetItem(item)) {
        return [];
    }
    const localizedPaths = Object.values(item.assetByLanguage)
        .map((asset) => (asset ? decodeURIComponent(asset?.fullpath.toLowerCase()) : undefined))
        .filter((p): p is string => Boolean(p));

    return Array.from(new Set(localizedPaths));
}

function splitSearchTerm(searchTerm: string): string[] {
    // Match quoted strings or individual words
    return Array.from(
        searchTerm.matchAll(/"([^"]+)"|(\S+)/g),
        (match) => match[1] || match[2]
    ).filter((term): term is string => Boolean(term));
}

function exactMatch(searchTerm: string, ...fields: string[]): boolean {
    return fields.some((field) => field === searchTerm);
}

function regexMatch(regex: RegExp, ...fields: string[]): boolean {
    return fields.some((field) => regex.test(field));
}

function substringMatch(searchTerm: string, ...fields: string[]): boolean {
    return fields.some((field) => field.includes(searchTerm));
}

// Function to check if all search terms are present
function allTermsMatch(item: ResourceLibraryItem, searchTerm: string): boolean {
    searchTerm = searchTerm.toLowerCase().trim();
    const searchTerms = splitSearchTerm(searchTerm);

    const name = item.name.toLowerCase();
    const description = item.description.toLowerCase();
    const key = item.key.toLowerCase();
    const assetPaths = getAssetPaths(item);

    for (const term of searchTerms) {
        if (
            !name.includes(term) &&
            !description.includes(term) &&
            !key.includes(term) &&
            !assetPaths.some((path) => path.includes(term))
        ) {
            return false;
        }
    }
    return true;
}

// Optimized function to calculate relevance score
function calculateRelevanceScore(
    item: ResourceLibraryItem,
    searchTerm: string
): { score: number; missingTerms: string[] } {
    const missingTerms: string[] = [];

    const name = item.name.toLowerCase();
    const description = item.description.toLowerCase();
    const key = item.key.toLowerCase();
    const assetPaths = getAssetPaths(item);
    const assetPathsText = assetPaths.join(' ');

    searchTerm = searchTerm.toLowerCase().trim();

    let score = 0;

    // Exact match check against keys, asset paths and names
    if (exactMatch(searchTerm, key, ...assetPaths, name)) {
        score += 1_000_000; // Full match boost
        return { score, missingTerms };
    }

    // Split search term into individual terms (preserving quoted phrases)
    const searchTerms = splitSearchTerm(searchTerm);
    const totalTerms = searchTerms.length;

    // Exact match after removing quotes
    if (
        totalTerms === 1 &&
        searchTerms[0] &&
        searchTerms[0] !== searchTerm &&
        exactMatch(searchTerms[0], key, ...assetPaths, name)
    ) {
        score += 900_000;
        return { score, missingTerms };
    }

    // Full term surrounded by word boundaries
    const fullRegex = new RegExp(`\\b${escapeRegExp(searchTerm)}\\b`, 'i');
    if (regexMatch(fullRegex, key, ...assetPaths, name)) {
        score += 500_000;
        return { score, missingTerms };
    }
    // for description use lower score
    if (regexMatch(fullRegex, description)) {
        score += 480_000; // Single term word boundary match boost
        return { score, missingTerms };
    }

    // Full term surrounded by word bounderies after removing quotes if the complete search term is quoted
    if (totalTerms === 1 && searchTerms[0] && searchTerms[0] !== searchTerm) {
        const fullRegex2 = new RegExp(`\\b${escapeRegExp(searchTerms[0])}\\b`, 'i');
        if (regexMatch(fullRegex2, key, ...assetPaths, name)) {
            score += 400_000;
            return { score, missingTerms };
        }
        // for description use lower score
        if (regexMatch(fullRegex2, description)) {
            score += 380_000; // Single term word boundary match boost
            return { score, missingTerms };
        }
    }

    // if there is a space in the search term, try to match the exact substring ignoring word boundaries;
    // don't do this for single-word search terms, as word boundaries (covered in later checks) are
    // more relevant there
    if (totalTerms > 1) {
        if (substringMatch(searchTerm, key, ...assetPaths, name)) {
            score += 300_000;
            return { score, missingTerms };
        }
        // for description use lower score
        if (substringMatch(searchTerm, description)) {
            score += 280_000;
            return { score, missingTerms };
        }
    }

    // Process inidividual search terms (split by spaces or quotes)

    // Match quoted strings or individual words
    let matchedTermsCount = 0;

    // Preprocess: Compile regexes for full word and partial matches
    const fullWordRegexes = searchTerms.map(
        (term) => new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i')
    );
    const partialRegexes = searchTerms.map((term) => new RegExp(escapeRegExp(term), 'i'));

    // Full word matches
    for (let i = 0; i < totalTerms; i++) {
        const term = searchTerms[i];
        const termPositionWeight = totalTerms - i; // Higher weight for earlier terms

        let termMatched = false;
        let termScore = 0;

        // Check for full word matches in name and description
        const fullWordRegex = fullWordRegexes[i];
        if (fullWordRegex?.test(assetPathsText)) {
            termScore += 20 * termPositionWeight; // Boost for asset paths
            termMatched = true;
        } else if (fullWordRegex?.test(key)) {
            termScore += 15 * termPositionWeight;
            termMatched = true;
        } else if (fullWordRegex?.test(name)) {
            termScore += 10 * termPositionWeight;
            termMatched = true;
        } else if (fullWordRegex?.test(description)) {
            termScore += 7 * termPositionWeight;
            termMatched = true;
        }

        // If no full word match, check for partial matches
        if (!termMatched) {
            const partialRegex = partialRegexes[i];
            if (partialRegex?.test(assetPathsText)) {
                termScore += 15 * termPositionWeight; // Boost for asset paths
                termMatched = true;
            } else if (partialRegex?.test(key)) {
                termScore += 10 * termPositionWeight;
                termMatched = true;
            } else if (partialRegex?.test(name)) {
                termScore += 5 * termPositionWeight;
                termMatched = true;
            } else if (partialRegex?.test(description)) {
                termScore += 3 * termPositionWeight;
                termMatched = true;
            }
        }

        if (termMatched) {
            score += termScore;
            matchedTermsCount++;
        } else {
            missingTerms.push(term || '');
        }
    }

    // If all terms are matched, boost the score significantly
    if (matchedTermsCount === totalTerms) {
        score += 1_000_000; // Boost value to surpass any partial matches
    }

    return { score, missingTerms };
}

// Utility function to escape special characters in regex patterns
function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Main function to filter items based on search terms and sorting method
export function filterItemsBySearchTerm<T extends ResourceLibraryItem>(
    items: T[],
    searchTerm: string | null,
    relevanceRequired: boolean
): SearchResult<T>[] {
    if (!searchTerm) {
        // If no search term, return all items with default relevance score
        return items.map((item) => ({ item, relevanceScore: 0, missingTerms: [] }));
    }

    let results: SearchResult<T>[] = [];

    if (relevanceRequired) {
        // Full relevance scoring
        for (const item of items) {
            const { score: relevanceScore, missingTerms } = calculateRelevanceScore(
                item,
                searchTerm
            );

            if (relevanceScore > 0) {
                results.push({ item, relevanceScore, missingTerms });
            }
        }
    } else {
        // Simplified filtering without relevance scoring
        for (const item of items) {
            if (allTermsMatch(item, searchTerm)) {
                results.push({ item, relevanceScore: 0, missingTerms: [] });
            }
        }
    }

    return results;
}
